import { Injectable, inject } from '@angular/core';
import { arrayRemove, arrayUnion, orderBy, where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { COLLECTIONS, ENVIRONMENT } from '../core.constants';
import { ExtraService } from '../models/edition.model';
import { FormConfig, FormSubmission } from '../models/forms.model';
import { FormInscription, Inscription } from '../models/inscription.model';
import { Payment } from '../models/payment.model';
import { AuthService } from '../services/auth.service';
import { FirestoreService } from '../services/firestore.service';
import { limit } from 'firebase/firestore';
import {
  Observable,
  combineLatest,
  first,
  map,
  of,
  shareReplay,
  switchMap,
  distinctUntilChanged,
  debounceTime,
} from 'rxjs';
import { EditionService } from './edition.service';

@Injectable({
  providedIn: 'root',
})
export class InscriptionService {
  auth = inject(AuthService);
  firestore = inject(FirestoreService);
  editionService = inject(EditionService);
  private functions = inject(Functions);
  private environment = inject(ENVIRONMENT);

  public allAccepted$ = this.editionService.currentEdition$.pipe(
    switchMap((edition) =>
      this.firestore.listDocumentsWithId<Inscription>(
        COLLECTIONS.INSCRIPTIONS,
        where('editionId', '==', edition.id),
        where('statusInscription', '==', 'ACCEPTED'),
      ),
    ),
    shareReplay(1),
  );

  public myCurrentInscription$: Observable<Inscription | null> = combineLatest([
    this.editionService.currentEdition$,
    this.auth.uid$,
  ]).pipe(
    switchMap(([edition, uid]) =>
      this.firestore.findDocument<Inscription>(
        COLLECTIONS.INSCRIPTIONS,
        where('summonedParticipants', 'array-contains', uid),
        where('editionId', '==', edition.id),
      ),
    ),
    shareReplay(1),
  );

  public myCurrentInscriptionIsAccepted$: Observable<Inscription | null> = combineLatest([
    this.editionService.currentEdition$,
    this.auth.uid$,
  ]).pipe(
    switchMap(([edition, uid]) =>
      this.firestore.findDocument<Inscription>(
        COLLECTIONS.INSCRIPTIONS,
        where('summonedParticipants', 'array-contains', uid),
        where('editionId', '==', edition.id),
        where('statusInscription', '==', 'ACCEPTED'),
        where('statusPayment', 'in', ['PARTIAL_PAID', 'PAID']),
      ),
    ),
    shareReplay(1),
  );

  public hasValidInscription$: Observable<boolean> = this.myCurrentInscription$.pipe(
    map(
      (inscription) =>
        !!inscription &&
        inscription.statusInscription === 'ACCEPTED' &&
        typeof inscription.statusPayment === 'string' &&
        ['PARTIAL_PAID', 'PAID'].includes(inscription.statusPayment),
    ),
  );

  /**
   * True when the user's team can access the booking/reservation flow.
   * If edition.enableBookings is true → everyone has access (current behavior).
   * If edition.enableBookings is false → only teams with inscription.bookingEnabled === true.
   */
  public bookingAccessEnabled$: Observable<boolean> = combineLatest([
    this.editionService.currentEdition$,
    this.myCurrentInscriptionIsAccepted$,
  ]).pipe(
    map(([edition, inscription]) => {
      if (edition.enableBookings) return true;
      return inscription?.bookingEnabled === true;
    }),
    shareReplay(1),
  );

  public myCurrentCategory$: Observable<string | null> = this.myCurrentInscription$.pipe(
    map((res) => res?.category ?? null),
  );

  public myCurrentPayments$ = this.myCurrentInscription$.pipe(
    switchMap((inscription) => {
      if (!inscription?.id) return of([]);
      return this.firestore.listDocumentsWithId<Payment>(
        COLLECTIONS.PAYMENTS,
        where('inscriptionId', '==', inscription.id),
        orderBy('createdAt', 'desc'),
      );
    }),
    shareReplay(1),
  );

  public editionForms$ = this.firestore.listDocuments<FormConfig>(
    COLLECTIONS.FORMS,
    where('type', '==', this.environment.appType),
    where('isVisible', '==', true),
    orderBy('order', 'asc'),
  );

  public myForms$ = combineLatest([this.editionForms$, this.myCurrentInscription$, this.auth.uid$]).pipe(
    switchMap(([editionForms, inscription, userId]) => {
      if (!editionForms.length) {
        return of([]);
      }

      if (!inscription?.id) {
        // Si no hay inscripción, devolver todos los formularios como no completados
        return of(
          editionForms.map((form) => ({
            ...form,
            fields: form.fields ? [...form.fields].sort((a, b) => a.order - b.order) : [],
            completed: false,
            submission: null,
          })),
        );
      }

      // Para cada formulario, escuchamos la colección de submissions completa
      // Esto mantiene la reactividad incluso cuando el documento no existe inicialmente
      const formChecks = editionForms.map((form) =>
        this.firestore
          .listDocuments<FormSubmission>(
            `${COLLECTIONS.INSCRIPTIONS}/${inscription.id}/${COLLECTIONS.INSCRIPTIONS_FORMS}/${form.id}/submissions`,
          )
          .pipe(
            map((submissions) => {
              // Buscar la submission del usuario actual
              const userSubmission = submissions.find((s) => s.id === userId) ?? null;

              let isCompleted = false;
              let relevantSubmission: FormSubmission | null = null;

              if (form.isByTeam) {
                // Si es por equipo, está completado si CUALQUIER miembro del equipo ha enviado
                isCompleted = submissions.length > 0 && submissions.some((s) => s.submittedAt !== undefined);
                // Usar la submission que tenga submittedAt (la primera que encontremos)
                relevantSubmission = submissions.find((s) => s.submittedAt !== undefined) ?? null;
              } else {
                // Si es individual, solo está completado si el usuario actual ha enviado
                isCompleted = userSubmission !== null && userSubmission.submittedAt !== undefined;
                relevantSubmission = userSubmission;
              }

              return {
                ...form,
                fields: form.fields ? [...form.fields].sort((a, b) => a.order - b.order) : [],
                completed: isCompleted,
                submission: relevantSubmission,
              };
            }),
            // Evitar emisiones duplicadas que causan parpadeo
            distinctUntilChanged(
              (prev, curr) => prev.completed === curr.completed && prev.submission?.id === curr.submission?.id,
            ),
          ),
      );

      return combineLatest(formChecks).pipe(
        // Debounce para evitar múltiples emisiones rápidas que causan parpadeo
        debounceTime(100),
        // Evitar emisiones duplicadas del array completo
        distinctUntilChanged(
          (prev, curr) =>
            JSON.stringify(prev.map((f) => ({ id: f.id, completed: f.completed }))) ===
            JSON.stringify(curr.map((f) => ({ id: f.id, completed: f.completed }))),
        ),
      );
    }),
    shareReplay(1),
  );

  getFormById(formId: string): Observable<FormConfig | undefined> {
    return this.editionForms$.pipe(map((forms) => forms.find((form) => form.id === formId)));
  }

  // Método para enviar un formulario
  submitForm(formId: string, data: any) {
    return combineLatest([this.myCurrentInscription$, this.auth.uid$]).pipe(
      first(),
      switchMap(([inscription, userId]) => {
        if (!inscription?.id) {
          throw new Error('No inscription found');
        }

        // Determinar el rol del participante basándose en el userId
        let participantRole: 'pilot' | 'copilot' | 'participant';
        if (inscription.pilot.id === userId) {
          participantRole = 'pilot';
        } else if (inscription.copilot?.id === userId) {
          participantRole = 'copilot';
        } else if (inscription.participants.some((p) => p.id === userId)) {
          participantRole = 'participant';
        } else {
          throw new Error('User not found in inscription');
        }

        const submission: Omit<FormSubmission, 'id'> = {
          formId,
          userId,
          participantRole,
          submittedAt: new Date(),
          data,
        };

        return this.firestore.createOrUpdate<FormSubmission>(
          `${COLLECTIONS.INSCRIPTIONS}/${inscription.id}/${COLLECTIONS.INSCRIPTIONS_FORMS}/${formId}/submissions`,
          userId,
          submission,
        );
      }),
    );
  }

  getParticipant(participantId: string) {
    return this.myCurrentInscription$.pipe(
      map(
        (inscriptions) =>
          [
            inscriptions!.pilot,
            ...(inscriptions!.copilot ? [inscriptions!.copilot] : []),
            ...inscriptions!.participants,
          ].find((p) => p.id === participantId)!,
      ),
    );
  }

  createInscriptionApp(inscriptionData: Omit<FormInscription, 'id' | 'userId' | 'editionId'>) {
    return combineLatest([this.editionService.currentEdition$, this.auth.uid$]).pipe(
      first(),
      switchMap(([edition, uid]) => {
        const copilotId = inscriptionData.copilot?.id;
        const participantIds = inscriptionData.participants?.map((participant) => participant.id) || [];

        const summonedParticipants = [uid, ...(copilotId ? [copilotId] : []), ...participantIds];

        return this.firestore.create<FormInscription>(COLLECTIONS.INSCRIPTIONS, {
          editionId: edition.id,
          ...inscriptionData,
          pilot: {
            ...inscriptionData.pilot,
            id: uid,
          },
          summonedParticipants,
          userId: uid,
        });
      }),
    );
  }

  createInscriptionWeb(
    inscriptionData: Omit<FormInscription, 'id' | 'userId' | 'editionId'>,
    appType: string,
    pass: string,
    language?: string,
  ) {
    const callable = httpsCallable(this.functions, 'createInscription');
    return callable({ inscriptionData, appType, pass, language });
  }

  getInscription(inscriptionId: string) {
    return this.firestore.getDocument<Inscription>(COLLECTIONS.INSCRIPTIONS, inscriptionId);
  }

  saveInscription(data: Inscription): Promise<void> {
    return this.firestore.update<Inscription>(COLLECTIONS.INSCRIPTIONS, {
      ...data,
      statusInscription: 'PENDING',
    });
  }

  updateInscription(inscriptionData: Partial<FormInscription>) {
    return this.firestore.update<Inscription>(COLLECTIONS.INSCRIPTIONS, inscriptionData);
  }

  addTeamPhoto(inscriptionId: string, photoUrl: string): Promise<void> {
    return this.firestore.update<Inscription>(COLLECTIONS.INSCRIPTIONS, {
      id: inscriptionId,
      teamPhotos: arrayUnion(photoUrl),
    } as any);
  }

  removeTeamPhoto(inscriptionId: string, photoUrl: string): Promise<void> {
    return this.firestore.update<Inscription>(COLLECTIONS.INSCRIPTIONS, {
      id: inscriptionId,
      teamPhotos: arrayRemove(photoUrl),
    } as any);
  }

  getInscriptionTotal(inscriptionId: string) {
    const callable = httpsCallable(this.functions, 'getInscriptionTotal');
    return callable({ inscriptionId }).then((result: any) => result.data);
  }

  validateEmails(editionId: string, emails: string[]) {
    const callable = httpsCallable(this.functions, 'validateEmails');
    return callable({ editionId, emails });
  }

  prepareExtraServices(
    services: { id: string; quantity: number; price: number }[],
    editionExtraServices: ExtraService[],
  ) {
    const filteredExtraServices = editionExtraServices.filter((s) => !s.payOffline);

    services = filteredExtraServices.map((extraService) => {
      const existingService = services.find((s) => s.id === extraService.id);

      return {
        id: extraService.id,
        quantity: existingService ? existingService.quantity : 1,
        price: extraService.price,
      };
    });

    return services;
  }

  validateDuplicatedEmails(inscription: any) {
    return this.editionService.currentEdition$.pipe(
      first(),
      switchMap((edition) => {
        const callable = httpsCallable(this.functions, 'validateDuplicatedEmails');
        return callable({ inscription, editionId: edition.id }).then(
          (result: any) =>
            result.data as {
              hasDuplicates: boolean;
              duplicates: { email: string; inscriptionIds: string[] }[];
            },
        );
      }),
    );
  }
}
